import React, { useCallback, useEffect, useRef, useState } from "react";
import AnimatedFeedbackPopup from "../AnimatedFeedbackPopup";
import "./BasketballStage.css";

/** Blur group id -> sharp line id (matches `Basketball.svg`). */
export const BASKETBALL_BLUR_TO_LINE = {
  Inside_Line_01_Blur: "Inside_Line_01",
  Right_Side_Line_blur: "Right_Side_Line",
  Left_side_line_blur: "Left_side_line",
  _x34__Line_Right_Blur: "_x34__Line_Right",
  _x34__Line_Left_Blur: "_x34__Line_Left",
  Center_Line_Blur: "Center_Line",
  Inside_Line_02_blur: "Inside_Line_02",
};

export const BASKETBALL_MOBILE_BLUR_TO_LINE = {
  "Curve-blur": "Curve",
  "Center_line-_blur": "Center_line",
  "Curve-dash_blur": "Curve-dash",
  Left_Blur: "Left1",
  Right_Blur: "Right1",
  "Stripes-left_blur": "Stripes-_left",
  "Stripes-right_blur": "Stripes-_right",
};

const POPUP_MS = 1000;
const COMPLETE_REDIRECT_MS = 1000;

function forceHideSvgNode(node) {
  if (!node) return;
  node.setAttribute("display", "none");
  node.setAttribute("visibility", "hidden");
  node.style.display = "none";
  node.style.visibility = "hidden";
  node.style.opacity = "0";
  node.style.pointerEvents = "none";
  if (node.querySelectorAll) {
    node.querySelectorAll("*").forEach((child) => {
      child.setAttribute("display", "none");
      child.setAttribute("visibility", "hidden");
      child.style.display = "none";
      child.style.visibility = "hidden";
      child.style.opacity = "0";
      child.style.pointerEvents = "none";
    });
  }
}

function forceShowSvgNode(node) {
  if (!node) return;
  node.removeAttribute("display");
  node.setAttribute("visibility", "visible");
  node.style.display = "inline";
  node.style.visibility = "visible";
  node.style.opacity = "1";
  node.style.pointerEvents = "none";
  if (node.querySelectorAll) {
    node.querySelectorAll("*").forEach((child) => {
      child.removeAttribute("display");
      child.setAttribute("visibility", "visible");
      child.style.display = "inline";
      child.style.visibility = "visible";
      child.style.opacity = "1";
      child.style.pointerEvents = "none";
    });
  }
}

function removeSvgNode(node) {
  if (!node || !node.parentNode) return;
  node.parentNode.removeChild(node);
}

function getSvgNodeById(root, id) {
  if (!root || !id) return null;
  if (typeof root.getElementById === "function") {
    return root.getElementById(id);
  }
  if (typeof root.querySelector !== "function") return null;
  const safeId = String(id).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return root.querySelector(`[id="${safeId}"]`);
}

export default function BasketballStage({ data, onDone, onSessionPointsChange }) {
  const objectRef = useRef(null);
  const inlineRootRef = useRef(null);
  const cleanupRef = useRef(null);
  const finishedRef = useRef(false);
  const popupTimerRef = useRef(null);
  const completeTimerRef = useRef(null);
  const [found, setFound] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [svgMarkup, setSvgMarkup] = useState("");

  const blurToLineMap = data.blurToLineMap || BASKETBALL_BLUR_TO_LINE;
  const pointsPerClick = data.pointsPerClick ?? 5;
  const totalPairs = Object.keys(blurToLineMap).length;
  const score = found.length * pointsPerClick;

  useEffect(() => {
    onSessionPointsChange?.(score);
  }, [score, onSessionPointsChange]);

  const showGif = useCallback(() => {
    if (popupTimerRef.current) {
      window.clearTimeout(popupTimerRef.current);
      popupTimerRef.current = null;
    }
    setPopupOpen(true);
    popupTimerRef.current = window.setTimeout(() => {
      setPopupOpen(false);
      popupTimerRef.current = null;
    }, POPUP_MS);
  }, []);

  useEffect(
    () => () => {
      if (popupTimerRef.current) window.clearTimeout(popupTimerRef.current);
      if (completeTimerRef.current) window.clearTimeout(completeTimerRef.current);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    setSvgMarkup("");
    const loadSvgInline = async () => {
      try {
        const res = await fetch(data.svgAsset, { cache: "no-store" });
        if (!res.ok) return;
        const text = await res.text();
        if (!cancelled && text && text.includes("<svg")) {
          setSvgMarkup(text);
        }
      } catch (_e) {
        // Fallback to <object> path below when inline load fails.
      }
    };
    loadSvgInline();
    return () => {
      cancelled = true;
    };
  }, [data.svgAsset]);

  useEffect(() => {
    const objectEl = objectRef.current;
    const inlineRoot = inlineRootRef.current;
    if (!data.svgAsset) return undefined;

    let wired = false;
    let retryTimer = null;
    let retryCount = 0;

    const handleLoad = () => {
      if (wired) return;
      const svgDoc = svgMarkup ? inlineRoot : objectEl?.contentDocument;
      if (!svgDoc) return;
      wired = true;

      const cleaners = [];

      Object.entries(blurToLineMap).forEach(([blurId, normalId]) => {
        const blurEl = getSvgNodeById(svgDoc, blurId);
        const normalEl = getSvgNodeById(svgDoc, normalId);
        forceHideSvgNode(normalEl);
        if (!blurEl) return;

        blurEl.removeAttribute("display");
        blurEl.setAttribute("visibility", "visible");
        blurEl.style.display = "inline";
        blurEl.style.visibility = "visible";
        blurEl.style.opacity = "1";
        blurEl.style.cursor = "pointer";
        blurEl.style.pointerEvents = "auto";

        const onClick = (e) => {
          e.stopPropagation();

          blurEl.removeEventListener("click", onClick);
          forceHideSvgNode(blurEl);
          removeSvgNode(blurEl);
          forceShowSvgNode(normalEl);

          setFound((prev) => {
            if (prev.includes(blurId)) return prev;
            const next = [...prev, blurId];
            if (next.length === totalPairs && !finishedRef.current) {
              finishedRef.current = true;
              if (completeTimerRef.current) {
                window.clearTimeout(completeTimerRef.current);
              }
              completeTimerRef.current = window.setTimeout(() => {
                onDone(totalPairs * pointsPerClick);
                completeTimerRef.current = null;
              }, COMPLETE_REDIRECT_MS);
            }
            return next;
          });
          showGif();
        };

        blurEl.addEventListener("click", onClick);
        cleaners.push(() => blurEl.removeEventListener("click", onClick));
      });

      cleanupRef.current = () => cleaners.forEach((fn) => fn());
    };

    const tryWire = () => {
      if (wired) return true;
      handleLoad();
      return wired;
    };

    if (objectEl && !svgMarkup) {
      objectEl.addEventListener("load", handleLoad);
    }
    // Immediate try for cached/fast-loaded SVGs.
    tryWire();
    // Production fallback: periodically retry in case load event fired before listener.
    retryTimer = window.setInterval(() => {
      retryCount += 1;
      if (tryWire() || retryCount > 40) {
        window.clearInterval(retryTimer);
        retryTimer = null;
      }
    }, 150);

    return () => {
      if (objectEl && !svgMarkup) {
        objectEl.removeEventListener("load", handleLoad);
      }
      if (retryTimer) {
        window.clearInterval(retryTimer);
        retryTimer = null;
      }
      if (cleanupRef.current) cleanupRef.current();
      cleanupRef.current = null;
    };
  }, [data.svgAsset, blurToLineMap, showGif, svgMarkup]);

  return (
    <div className="basketball-stage basketball-stage--bleed">
      <div className="basketball-stage__svg-wrap">
        {svgMarkup ? (
          <div
            ref={inlineRootRef}
            className="basketball-stage__inline-svg"
            dangerouslySetInnerHTML={{ __html: svgMarkup }}
          />
        ) : (
          <object ref={objectRef} data={data.svgAsset} type="image/svg+xml" className="basketball-stage__object">
            Basketball court
          </object>
        )}
      </div>
      <AnimatedFeedbackPopup open={popupOpen} type="success" title="Awesome!" />
    </div>
  );
}
